import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import general from "../../../general";
import Dialog from "../../../lib/Dialog";
import { NavLink } from "../../../lib/Navigation";
import URI from "urijs";
import "./css/GoodGameAssist.css";
import useActiveState from "../../../lib/useActiveState";

/**
 * discount 字段（如 "0.010"）转换为折扣显示（如 "0.1折"）
 * 1折 = 10% 原价，discount 值 × 10 = 折数
 */
function formatDiscount(discount) {
  const val = parseFloat(discount) * 10;
  if (!val || val >= 10) return null;
  const str = parseFloat(val.toFixed(2)).toString();
  return str;
}

/**
 * introduction 字段以 \n 分割，去掉空行和多余空格，返回字符串数组
 */
function parseIntroduction(intro) {
  if (!intro) return [];
  return intro
    .split("\n")
    .map((s) => s.trim().replace(/^·\s*/, ""))
    .filter(Boolean);
}

export default function GoodGameAssist(props) {
  const history = useHistory();

  const [activeTab,  setActiveTab]  = useState(1);
  const [bannerImg,  setBannerImg]  = useState("");
  const [bgColor,    setBgColor]    = useState("#5781E7");
  const [tabs,       setTabs]       = useState([]);
  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [loadingMore, setLoadingMore] = useState(false); // 仅用于防止重复请求
  const [hasMore,    setHasMore]    = useState(false);
  const [inited,     setInited]     = useState(false);
  const initedRef    = useRef(false); // ref 版，供 load 闭包读取最新值（避免 stale closure）
  const pageRef      = useRef(1);
  const sentinelRef  = useRef(null);   // 列表底部哨兵元素
  const observerRef  = useRef(null);   // IntersectionObserver 实例
  const loadingRef   = useRef(false);  // 防止重复触发
  const [agent]      = useActiveState('agent');

  // 请求列表数据
  const load = useCallback((tab, page = 1) => {
    if (page === 1) {
      setLoading(true);
      // 仅首次（initedRef=false）才显示全局 loading，返回后重进不再弹
      if (!initedRef.current) Dialog.loading();
    } else {
      setLoadingMore(true);
    }

    general.axios
      .post("/getGoodGameAssist", {
        type: tab,
        pagination: { page, count: 10 },
      })
      .finally(() => {
        loadingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
        if (!initedRef.current) Dialog.close();
      })
      .then((res) => {
        const { status, data } = res.data;
        if (!status.succeed) {
          Dialog.error(status.error_desc);
          return;
        }
        initedRef.current = true;
        setInited(true);
        if (page === 1) {
          setBannerImg(data.top    || "");
          setBgColor(data.bgcolor  || "#5781E7");
          setTabs([
            {
              type:      1,
              img:       data.left_action_icon_noselect  || "",
              imgActive: data.left_action_icon_select    || "",
            },
            {
              type:      2,
              img:       data.right_action_icon_noselect || "",
              imgActive: data.right_action_icon_select   || "",
            },
          ]);
          setList(data.list        || []);
        } else {
          setList((prev) => [...prev, ...(data.list || [])]);
        }
        setHasMore(data.paginated?.more === 1);
        pageRef.current = page;
      })
      .catch((err) => {
        Dialog.error(err.message || "加载失败");
      });
  // eslint-disable-next-line
  }, []);

  // 页面激活 / 切换 tab 时重新拉取第一页
  useEffect(() => {
    if (props.active) {
      load(activeTab, 1);
    }
  }, [props.active, activeTab]);

  // 切换 tab
  const handleTabChange = (type) => {
    if (type === activeTab) return;
    setActiveTab(type);
    setList([]);
    setHasMore(false);
  };

  // 滚动到底自动加载更多（IntersectionObserver）
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          // hasMore / loadingMore 通过 ref 读取，避免闭包陈旧值
          setHasMore((more) => {
            if (more && !loadingRef.current) {
              loadingRef.current = true;
              setLoadingMore(true);
              // activeTab / pageRef 是最新值，直接使用
              load(activeTab, pageRef.current + 1);
            }
            return more;
          });
        }
      },
      { threshold: 0.1 }
    );
    return () => observerRef.current?.disconnect();
  // eslint-disable-next-line
  }, [activeTab, load]);

  // 哨兵元素挂载/卸载时绑定 Observer
  const sentinelCallbackRef = useCallback((el) => {
    sentinelRef.current = el;
    if (observerRef.current) {
      observerRef.current.disconnect();
      if (el) observerRef.current.observe(el);
    }
  }, []);

  // 点击"去参与"跳转游戏详情
  const handleJoin = (item) => {
    const hashSearch = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
    const downloadSourceId = new URLSearchParams(hashSearch).get('download_source_id');
    const url = "/game?id=" + item.game_id + (downloadSourceId ? "&download_source_id=" + downloadSourceId : "");
    history.push(url);
  };

  return (
    <div className="good-game-assist" style={{display:inited ? 'block' : 'none'}}>
      <div className="good-game-assist-container" style={{backgroundImage:`url(${bannerImg})`,backgroundColor:bgColor}}>

      {/* ── Tab 切换（图片按钮）──────────────────────────────── */}
      <div className="gga-tabs">
        {tabs.map((tab) => (
          <div
            key={tab.type}
            className="gga-tab"
            onClick={() => handleTabChange(tab.type)}
          >
            <img
              src={activeTab === tab.type ? tab.imgActive : tab.img}
              alt=""
              className="gga-tab-img"
            />
          </div>
        ))}
      </div>

      {/* ── 列表 ─────────────────────────────────────────────── */}
      {list.length === 0 && !loading && (
        <div className="gga-empty">暂无数据</div>
      )}
      {list.length > 0 && (
        <>
          <div className="gga-list">
            {list.map((item) => {
              const discount = formatDiscount(item.discount);
              const tags     = item.game_classify_type?.trim().replace(/\s+/g, " ") || "";
              const descs    = parseIntroduction(item.introduction);
              return (
                <NavLink key={item.id} className="gga-card" to={(() => {
                  
                  let url = URI(item.url)
                    .addQuery('title', item.title)
                    .addQuery('username', localStorage.getItem('username'))
                    .addQuery('token', localStorage.getItem('token'))
                    .addQuery('location_url', window.location.origin)
                    .addQuery('agent', agent)
                    .addQuery('from', 'react')
                    .toString();
                  return url;
                })()} iframe={{ title: item.title }} user={false}>
                  {/* 卡片头部 */}
                  <div className="gga-card-head">
                    <div onClick={(e)=> {
                      e.preventDefault();
                      handleJoin(item)
                    }}>
                      <img
                        className="gga-card-icon"
                        src={item.game_image?.thumb || ""}
                        alt={item.game_name}
                      />
                    </div>
                    <div className="gga-card-info">
                      <div className="gga-card-name">{item.game_name}</div>
                      <div className="gga-card-meta">
                        {discount && (
                          <span className="gga-card-discount">{discount}<span>折</span></span>
                        )}
                        {tags && (
                          <span className="gga-card-tags">{tags}</span>
                        )}
                      </div>
                    </div>
                    <div className="gga-card-btn">去参与&gt;</div>
                  </div>

                  {/* 分隔线 */}
                  <div className="gga-card-divider" />

                  {/* 活动描述要点 */}
                  {descs.length > 0 && (
                    <div className="gga-card-descs">
                      {descs.map((desc, i) => (
                        <div key={i} className="gga-card-desc-item">
                          {desc}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 活动结束时间 */}
                  {item.end_time && (
                    <div className="gga-card-expire">{item.end_time}</div>
                  )}
                </NavLink>
              );
            })}

            {/* 底部哨兵：进入视口时自动加载下一页（不含视觉元素） */}
            {hasMore && (
              <div ref={sentinelCallbackRef} className="gga-sentinel" />
            )}
            {!hasMore && (
              <div className="gga-no-more">已加载全部</div>
            )}
          </div>
        </>
      )}
    </div>
    </div>
  );
}
